/** Waitlist identifiers belong to booking, not pageview or attribution data. */
export function stripWaitlistIdFromUrl(value: string): string {
  return value
    .replace(
      /(\/patient\/self-schedule\/waitlist\/)[^/?#\s"'<>]+(\/booking-preferences)/g,
      "$1[redacted]$2",
    )
    .replace(/\?[^#\s"'<>]*/g, (query) => {
      const parts = query.slice(1).split("&");
      if (!parts.some((part) => part.split("=")[0] === "waitlistId")) {
        return query;
      }
      const retained = parts.filter(
        (part) => part && part.split("=")[0] !== "waitlistId",
      );
      return retained.length ? `?${retained.join("&")}` : "";
    });
}

/**
 * Covers nested PostHog URL properties and captured network metadata.
 *
 * This runs on every PostHog event, session-replay snapshots included, so it
 * hands back the original value whenever there is nothing to redact. Copying
 * each replay batch while someone types is the suspected cause of Safari
 * killing the note editor tab (Pylon #7464, not reproduced).
 */
export function sanitizeWaitlistLinkForAnalytics<T>(value: T): T {
  if (typeof value === "string") {
    // Both patterns stripWaitlistIdFromUrl redacts contain "waitlist".
    return (
      value.includes("waitlist") ? stripWaitlistIdFromUrl(value) : value
    ) as T;
  }
  if (Array.isArray(value)) {
    let changed: unknown[] | undefined;
    value.forEach((item, index) => {
      const sanitized = sanitizeWaitlistLinkForAnalytics(item);
      if (sanitized !== item) {
        changed ??= value.slice();
        changed[index] = sanitized;
      }
    });
    return (changed ?? value) as T;
  }
  if (
    value &&
    typeof value === "object" &&
    Object.getPrototypeOf(value) === Object.prototype
  ) {
    const source = value as Record<string, unknown>;
    let copy: Record<string, unknown> | undefined;
    for (const key of Object.keys(source)) {
      const item = source[key];
      if (key.toLowerCase() === "waitlistid") {
        copy ??= { ...source };
        delete copy[key];
        continue;
      }
      const sanitized = sanitizeWaitlistLinkForAnalytics(item);
      if (sanitized !== item) {
        copy ??= { ...source };
        copy[key] = sanitized;
      }
    }
    return (copy ?? value) as T;
  }
  return value;
}
